// components/island/useAnrufe.ts
// Hält das Realtime-Abo auf `anrufe` und schiebt den aktiven Anruf in die Pille.
// Bewusst dünn: die Entscheidungen stecken in lib/telefonie/anrufWaechter.ts.
//
// Die Karte erscheint bei ALLEN Mitarbeitern, nicht nur bei dem, dessen Telefon
// klingelt. Das ist Absicht: Anrufe laufen über die Warteschlange und klingeln
// ohnehin bei mehreren gleichzeitig. Sobald profiles.webex_person_id befüllt ist,
// kann daraus eine Einschränkung werden.
import { useEffect, useRef } from 'react';
import { supabase, isDemoMode } from '../../lib/supabase/client';
import { useIslandActions, useIslandState } from './IslandProvider';
import {
  naechsteKarte, AUSBLENDEN_NACH_MS, istVerworfen, naechsteVerwerfung, verwerfenBeimSchliessen,
  verpasstSchliesstKarte,
} from '../../lib/telefonie/anrufWaechter';
import type { AnrufZeile } from '../../lib/telefonie/anrufKarte';

export function useAnrufe(aktiv: boolean) {
  const { zeigeAnruf, schliesseAnruf } = useIslandActions();
  const { anruf } = useIslandState();
  const gezeigteId = useRef<string | null>(null);
  const ausblendTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Anruf-ID, die gerade als "weggeklickt" gilt — Ereignisse dazu werden
  // übersprungen, siehe lib/telefonie/anrufWaechter.ts.
  const verworfenId = useRef<string | null>(null);
  // ID, die der eigene Ausblend-Timer zuletzt geräumt hat (oder null). Ein
  // ID-Vergleich statt eines Wahrheitswerts: Wird der Timer durch einen
  // manuellen Klick VOR seinem Ablauf überholt, feuert er 8 Sekunden später
  // trotzdem noch — ein bloßes Flag bliebe dann fälschlich "true" stehen und
  // würde beim NÄCHSTEN Anruf einen manuellen Klick fälschlich für "vom Timer"
  // halten. Siehe verwerfenBeimSchliessen in anrufWaechter.ts.
  const vomTimerGeraeumteId = useRef<string | null>(null);
  const vorherigeAnrufId = useRef<string | null>(null);

  // Erkennt den Übergang von "Karte war da" auf "Karte ist weg" — egal ob
  // Schließen-Knopf, oeffneKontakt() oder der eigene Timer der Auslöser war.
  // Nur wenn NICHT der eigene Timer für GENAU diese ID geräumt hat, gilt der
  // Anruf als verworfen.
  useEffect(() => {
    const jetzigeId = anruf?.id ?? null;
    if (vorherigeAnrufId.current && !jetzigeId) {
      verworfenId.current = verwerfenBeimSchliessen(vorherigeAnrufId.current, vomTimerGeraeumteId.current);
    }
    vorherigeAnrufId.current = jetzigeId;
  }, [anruf]);

  useEffect(() => {
    if (!aktiv || isDemoMode()) return;

    const kanal = supabase
      .channel('anrufe-pille')
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'anrufe' },
        (nachricht) => {
          const zeile = nachricht.new as AnrufZeile | undefined;
          if (!zeile?.id) return;

          verworfenId.current = naechsteVerwerfung(verworfenId.current, zeile.id);
          if (istVerworfen(verworfenId.current, zeile.id)) return;

          // Wird der gezeigte Anruf verpasst, geht die Karte sofort zu, statt noch
          // acht Sekunden „Verpasst" zu melden. Wie beim Ausblend-Timer vermerkt:
          // dieses Schließen ist keine manuelle Verwerfung durch den Nutzer.
          if (verpasstSchliesstKarte(gezeigteId.current, zeile)) {
            if (ausblendTimer.current) clearTimeout(ausblendTimer.current);
            gezeigteId.current = null;
            vomTimerGeraeumteId.current = zeile.id;
            schliesseAnruf();
            return;
          }

          const karte = naechsteKarte(gezeigteId.current, zeile);
          if (!karte) return;

          gezeigteId.current = karte.id;
          zeigeAnruf(karte);

          if (ausblendTimer.current) clearTimeout(ausblendTimer.current);
          if (karte.erledigt) {
            ausblendTimer.current = setTimeout(() => {
              gezeigteId.current = null;
              vomTimerGeraeumteId.current = karte.id;
              schliesseAnruf();
            }, AUSBLENDEN_NACH_MS);
          }
        },
      )
      .subscribe();

    return () => {
      if (ausblendTimer.current) clearTimeout(ausblendTimer.current);
      void supabase.removeChannel(kanal);
    };
  }, [aktiv, zeigeAnruf, schliesseAnruf]);
}
